import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { XIcon } from "lucide-react";
import { EnquiryForm } from "./EnquiryForm";
export function ExpressionOfInterestDialog() {
  const [open, setOpen] = useState(false);
  const [interest, setInterest] = useState("");
  const [session, setSession] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-open-eoi]");
      if (!trigger) return;
      event.preventDefault();
      setInterest(trigger.dataset.interest ?? "");
      setSession(value => value + 1);
      setOpen(true);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  useEffect(() => {
    if (!open) return;
    const previousActive = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])')).filter(element => !element.hasAttribute("disabled"));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
      previousActive?.focus();
    };
  }, [open]);
  return <AnimatePresence data-id="element-70" data-mp="1wocrbkep6ftu">
      {open && <motion.div key="eoi-overlay" className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 sm:items-center sm:p-6" role="presentation" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onMouseDown={event => event.target === event.currentTarget && setOpen(false)} data-id="element-71" data-mp="1wriid56vzrs7">
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="eoi-title" className="max-h-[94vh] w-full max-w-2xl overflow-y-auto bg-white text-black sm:max-h-[90vh]" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-72" data-mp="1i1jj0z3liab5">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/15 bg-white px-6 py-4 sm:px-8" data-id="element-73" data-mp="udeaoty6g32n">
              <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.14em] text-[#005A73]" data-id="element-74" data-mp="18buiko7rzzmd">Register your interest</p>
              <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Close" className="inline-flex h-11 w-11 items-center justify-center border border-black/30 transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-black hover:text-white active:scale-95" data-id="element-75" data-mp="13dj8qdhhdp45">
                <XIcon aria-hidden="true" size={21} data-id="element-76" data-mp="1n1sclksdzjjh" />
              </button>
            </div>
            <div className="px-6 py-8 sm:px-8 sm:py-10" data-id="element-77" data-mp="3gkits7buvoin">
              <h2 id="eoi-title" className="text-3xl font-bold leading-tight tracking-[-0.03em] sm:text-4xl" data-id="element-78" data-mp="3gjzmrlnqukje">Tell us where you could fit.</h2>
              {interest && <p className="mt-3 text-base text-black/70" data-id="element-79" data-mp="3qakkl6vlfl19">Area selected: <span className="font-semibold text-black" data-id="element-80" data-mp="3qb3zeepy5tb2">{interest}</span></p>}
              <div className="mt-7" data-id="element-81" data-mp="3v83xa2cn7pr3">
                <EnquiryForm key={session} idPrefix="dialog" initialInterest={interest} onClose={() => setOpen(false)} data-id="element-82" data-mp="3vb85rbx557nk" />
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}